import { EntryEditError } from '../edits.ts';
import { javaIsBlank } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection } from '../sql.ts';
import { REGION_MIN_POINTS, type MarkerStyle } from './atlas.ts';
import { encodePoints, type MapPoint } from './geometry.ts';

/**
 * Editing a map (docs/PARITY-STEP-4-PLAN.md section 5), as the desktop's `AtlasService` and
 * `AtlasRepository` save it:
 * - a new pin is unlabelled, shown to players, in the category last used (if any);
 * - a region needs at least three corners, and starts in its category's colour;
 * - a pin or region can't open the map it's on;
 * - a category is shared by every map; deleting one leaves its pins and regions
 *   uncategorized (the schema's `ON DELETE SET NULL`);
 * - deleting a pin unlinks the Chronology events that named it.
 */

export const MAP_EDIT_LIMITS = { label: 200, notes: 20_000, icon: 200, coordinate: 1_000_000, points: 2_000 } as const;
/** The colour a new category starts with (`askCategoryDetails`). */
export const NEW_CATEGORY_COLOR = '#3388CC';

export interface PinDetails {
  readonly label: string;
  readonly categoryId: string | null;
  readonly notes: string;
  readonly visible: boolean;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
  /** Its own icon (see `parseIconRef`); blank for its category's. */
  readonly icon: string;
  readonly style: MarkerStyle;
}

export interface RegionDetails {
  readonly label: string;
  readonly categoryId: string | null;
  /** Its own colour; null for its category's. */
  readonly colorHex: string | null;
  readonly notes: string;
  readonly visible: boolean;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
}

export interface CategoryDetails {
  readonly name: string;
  readonly colorHex: string;
  readonly icon: string;
}

// ---- pins ----

export function createPin(conn: SqlConnection, mapId: string, at: MapPoint, categoryId: string | null, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  checkPoint(at);
  transaction(conn, () => {
    mustExist(conn, 'atlas_maps', mapId, 'That map no longer exists.');
    if (categoryId !== null) mustExist(conn, 'marker_categories', categoryId, 'That category no longer exists.');
    conn.run(
      `INSERT INTO map_markers (x, y, label, category_id, notes, visible, linked_entry_id, linked_map_id, icon, style, created, modified, map_id, id)
       VALUES (?, ?, '', ?, '', 1, NULL, NULL, '', 'PIN', ?, ?, ?, ?)`,
      [at.x, at.y, categoryId, now, now, mapId, id],
    );
  });
  return id;
}

/** Drag to move (`AtlasService.moveMarker`). */
export function movePin(conn: SqlConnection, id: string, at: MapPoint, now: string = new Date().toISOString()): void {
  checkPoint(at);
  transaction(conn, () => {
    mustExist(conn, 'map_markers', id, 'That pin no longer exists.');
    conn.run('UPDATE map_markers SET x = ?, y = ?, modified = ? WHERE id = ?', [at.x, at.y, now, id]);
  });
}

export function updatePin(conn: SqlConnection, id: string, details: PinDetails, now: string = new Date().toISOString()): void {
  checkText(details.label, details.notes);
  if (details.icon.length > MAP_EDIT_LIMITS.icon) throw new EntryEditError('invalid', 'That icon is too long.');
  if (details.style !== 'PIN' && details.style !== 'TOKEN') throw new EntryEditError('invalid', 'A pin shows as a pin or a token.');
  transaction(conn, () => {
    const mapId = mapOf(conn, 'map_markers', id, 'That pin no longer exists.');
    if (details.linkedMapId === mapId) throw new EntryEditError('invalid', "A pin can't open the map it's on.");
    checkLinks(conn, details.categoryId, details.linkedEntryId, details.linkedMapId);
    conn.run('UPDATE map_markers SET label = ?, category_id = ?, notes = ?, visible = ?, linked_entry_id = ?, linked_map_id = ?, icon = ?, style = ?, modified = ? WHERE id = ?', [
      details.label,
      details.categoryId,
      details.notes,
      details.visible ? 1 : 0,
      details.linkedEntryId,
      details.linkedMapId,
      details.icon.trim(),
      details.style,
      now,
      id,
    ]);
  });
}

/** Deletes a pin; returns how many Chronology events named it. */
export function deletePin(conn: SqlConnection, id: string): number {
  return transaction(conn, () => {
    mustExist(conn, 'map_markers', id, 'That pin no longer exists.');
    const events = int(scalar(conn, 'SELECT COUNT(*) FROM chronology_event_markers WHERE marker_id = ?', [id]));
    conn.run('DELETE FROM map_markers WHERE id = ?', [id]);
    return events;
  });
}

// ---- regions ----

export function createRegion(conn: SqlConnection, mapId: string, points: readonly MapPoint[], categoryId: string | null, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  checkOutline(points);
  transaction(conn, () => {
    mustExist(conn, 'atlas_maps', mapId, 'That map no longer exists.');
    if (categoryId !== null) mustExist(conn, 'marker_categories', categoryId, 'That category no longer exists.');
    conn.run(
      `INSERT INTO map_regions (label, points, category_id, color, notes, visible, linked_entry_id, linked_map_id, modified, created, map_id, id)
       VALUES ('', ?, ?, NULL, '', 1, NULL, NULL, ?, ?, ?, ?)`,
      [encodePoints(points), categoryId, now, now, mapId, id],
    );
  });
  return id;
}

export function updateRegion(conn: SqlConnection, id: string, details: RegionDetails, now: string = new Date().toISOString()): void {
  checkText(details.label, details.notes);
  if (details.colorHex !== null && !/^#[0-9A-Fa-f]{6}$/.test(details.colorHex)) throw new EntryEditError('invalid', 'That is not a colour.');
  transaction(conn, () => {
    const mapId = mapOf(conn, 'map_regions', id, 'That region no longer exists.');
    if (details.linkedMapId === mapId) throw new EntryEditError('invalid', "A region can't open the map it's drawn on.");
    checkLinks(conn, details.categoryId, details.linkedEntryId, details.linkedMapId);
    conn.run('UPDATE map_regions SET label = ?, category_id = ?, color = ?, notes = ?, visible = ?, linked_entry_id = ?, linked_map_id = ?, modified = ? WHERE id = ?', [
      details.label,
      details.categoryId,
      details.colorHex,
      details.notes,
      details.visible ? 1 : 0,
      details.linkedEntryId,
      details.linkedMapId,
      now,
      id,
    ]);
  });
}

export function deleteRegion(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'map_regions', id, 'That region no longer exists.');
    conn.run('DELETE FROM map_regions WHERE id = ?', [id]);
  });
}

// ---- categories ----

export function createCategory(conn: SqlConnection, details: CategoryDetails, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = cleanCategory(details);
  conn.run('INSERT INTO marker_categories (name, color, icon, created, id) VALUES (?, ?, ?, ?, ?)', [clean.name, clean.colorHex, clean.icon, now, id]);
  return id;
}

export function updateCategory(conn: SqlConnection, id: string, details: CategoryDetails): void {
  const clean = cleanCategory(details);
  transaction(conn, () => {
    mustExist(conn, 'marker_categories', id, 'That category no longer exists.');
    conn.run('UPDATE marker_categories SET name = ?, color = ?, icon = ? WHERE id = ?', [clean.name, clean.colorHex, clean.icon, id]);
  });
}

/** How many pins and regions, on every map, a category's deletion leaves uncategorized. */
export function categoryUse(conn: SqlConnection, id: string): number {
  return int(scalar(conn, 'SELECT (SELECT COUNT(*) FROM map_markers WHERE category_id = ?) + (SELECT COUNT(*) FROM map_regions WHERE category_id = ?)', [id, id]));
}

export function deleteCategory(conn: SqlConnection, id: string): number {
  return transaction(conn, () => {
    mustExist(conn, 'marker_categories', id, 'That category no longer exists.');
    const used = categoryUse(conn, id);
    conn.run('DELETE FROM marker_categories WHERE id = ?', [id]);
    return used;
  });
}

// ---- helpers ----

function checkPoint(p: MapPoint): void {
  if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || Math.abs(p.x) > MAP_EDIT_LIMITS.coordinate || Math.abs(p.y) > MAP_EDIT_LIMITS.coordinate) {
    throw new EntryEditError('invalid', 'That is not a place on the map.');
  }
}

function checkOutline(points: readonly MapPoint[]): void {
  if (points.length < REGION_MIN_POINTS) throw new EntryEditError('invalid', `A region needs at least ${REGION_MIN_POINTS} points.`);
  if (points.length > MAP_EDIT_LIMITS.points) throw new EntryEditError('invalid', `A region can have at most ${MAP_EDIT_LIMITS.points.toLocaleString('en')} corners.`);
  points.forEach(checkPoint);
}

function checkText(label: string, notes: string): void {
  if (label.length > MAP_EDIT_LIMITS.label) throw new EntryEditError('invalid', `A name can be at most ${MAP_EDIT_LIMITS.label} characters.`);
  if (notes.length > MAP_EDIT_LIMITS.notes) throw new EntryEditError('invalid', `Notes can be at most ${MAP_EDIT_LIMITS.notes.toLocaleString('en')} characters.`);
}

function checkLinks(conn: SqlConnection, categoryId: string | null, entryId: string | null, mapId: string | null): void {
  if (categoryId !== null) mustExist(conn, 'marker_categories', categoryId, 'That category no longer exists.');
  if (entryId !== null) mustExist(conn, 'lore_entries', entryId, 'That entry no longer exists.');
  if (mapId !== null) mustExist(conn, 'atlas_maps', mapId, 'That map no longer exists.');
}

function cleanCategory(details: CategoryDetails): CategoryDetails {
  if (details.name.length > MAP_EDIT_LIMITS.label) throw new EntryEditError('invalid', `A name can be at most ${MAP_EDIT_LIMITS.label} characters.`);
  if (!/^#[0-9A-Fa-f]{6}$/.test(details.colorHex)) throw new EntryEditError('invalid', 'That is not a colour.');
  if (details.icon.length > MAP_EDIT_LIMITS.icon) throw new EntryEditError('invalid', 'That icon is too long.');
  return { name: javaIsBlank(details.name) ? 'Category' : details.name.replace(/[\r\n]+/g, ' '), colorHex: details.colorHex, icon: details.icon.trim() };
}

/** The map a pin or region is on. `table` is always one of this module's literals. */
function mapOf(conn: SqlConnection, table: string, id: string, message: string): string {
  const row = conn.all(`SELECT map_id FROM ${table} WHERE id = ?`, [id])[0];
  if (!row) throw new EntryEditError('not_found', message);
  return text(row['map_id']);
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
